import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import { googleReviewsPageSchema } from '../components/StructuredData';
import './Features.css';

const GoogleReviews: React.FC = () => {
  return (
    <div className="feature-page">
      <SEO
        title="Google Reviews - TableChat | Grow Positive Reviews During the Meal"
        description="Grow your restaurant's positive Google reviews by making it easy for happy guests to leave feedback — naturally, during the meal. TableChat shares your Google review link when guests express genuine enthusiasm, with no awkward ask and no review gating."
        canonical="https://tablechat.io/features/google-reviews"
      />
      <StructuredData data={googleReviewsPageSchema} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Features', url: 'https://tablechat.io/features/google-reviews' },
        { name: 'Google Reviews', url: 'https://tablechat.io/features/google-reviews' },
      ])} />

      {/* Hero */}
      <section className="feature-hero feature-hero-split">
        <div className="container">
          <div className="feature-hero-grid">
            <div className="feature-hero-text">
              <h1>Grow Your Positive Google Reviews — Without Asking</h1>
              <p className="hero-lead">
                The best time to get a great review is when the guest is still at the table, enjoying 
                the experience. TableChat makes it effortless — when a guest expresses genuine enthusiasm 
                in the chat, TableChat naturally shares your Google review link. No server pitch, no printed 
                cards, no post-visit email that gets ignored.
              </p>
            </div>
            <div className="feature-hero-image">
              <img 
                src="/google-reviews-hero.png" 
                alt="Couple enjoying dinner at a restaurant while leaving a 5-star Google review on their phone" 
                className="feature-hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem-Solution Blocks */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>Reviews That Come from Real Moments</h2>
            <p>
              Most restaurants struggle to get Google reviews because the ask happens at the wrong time — 
              or not at all. TableChat puts the review opportunity right where the positive sentiment 
              lives: in the conversation, during the meal.
            </p>
          </div>

          <div className="feature-blocks">
            {/* Natural Review Prompts */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Naturally Timed, Never Forced</h3>
                <p>
                  When a guest says something like "this place is amazing!" or "best meal I've had in a 
                  long time" in the chat, TableChat recognizes the genuine enthusiasm and naturally weaves 
                  your Google review link into its warm response. It feels like a natural part of the 
                  conversation — not a sales pitch.
                </p>
                <p>
                  TableChat never asks for a review out of the blue. It only responds to heartfelt, spontaneous 
                  compliments — not casual remarks like "sounds good" or "thanks." This keeps the experience 
                  authentic and the reviews genuine.
                </p>
              </div>
              <div className="feature-visual">
                <h4>How It Works</h4>
                <ul>
                  <li>Guest expresses genuine positive sentiment in chat</li>
                  <li>TableChat includes Google review link in its warm response</li>
                  <li>Guest taps the link and leaves a review on the spot</li>
                  <li>Review link is shared at most once per session</li>
                  <li>Never pushed — only offered when the moment is right</li>
                </ul>
              </div>
            </div>

            {/* Google Compliance */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>Fully Compliant with Google's Policies</h3>
                <p>
                  Google prohibits "review gating" — selectively soliciting reviews only from happy 
                  customers while filtering out unhappy ones. TableChat doesn't gate. Any guest can ask 
                  for the review link at any time and receive it, regardless of sentiment.
                </p>
                <p>
                  The proactive sharing is triggered by the guest's own words, not by a filtered satisfaction 
                  score. And guests who ask directly — "where can I leave a review?" — always get the link. 
                  This approach is transparent, compliant, and builds trust with both Google and your guests.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Compliance Built In</h4>
                <ul>
                  <li>No review gating — any guest can request the link</li>
                  <li>No selective solicitation or satisfaction filtering</li>
                  <li>Guest-initiated review requests always honored</li>
                  <li>Abusive messages are filtered for spam, not sentiment</li>
                  <li>Transparent and aligned with Google's guidelines</li>
                </ul>
              </div>
            </div>

            {/* Simple Setup */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>One-Time Setup, Ongoing Results</h3>
                <p>
                  Getting started takes less than a minute. Paste your restaurant's Google review link 
                  into the TableChat management console and you're done. From that point forward, every 
                  guest conversation becomes an opportunity for a genuine review — without any staff 
                  involvement or manual follow-up.
                </p>
                <p>
                  Your staff doesn't need to remember to ask, hand out cards, or send follow-up emails. 
                  The review opportunity is woven into the guest experience automatically — and it happens 
                  while the guest is still happy and seated.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Setup &amp; Configuration</h4>
                <ul>
                  <li>Paste your Google review URL in the console</li>
                  <li>TableChat handles the rest — no staff training needed</li>
                  <li>Works for every table, every shift, every day</li>
                  <li>No printed materials or QR codes required</li>
                  <li>Active from the moment you save</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="feature-stats">
        <div className="container">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-number">In-Meal</div>
              <p className="stat-label">Review prompt happens while the guest is still at the table</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">1x</div>
              <p className="stat-label">Review link shared at most once per guest session</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">0</div>
              <p className="stat-label">Staff effort required — TableChat handles everything</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="feature-how-it-works">
        <div className="container">
          <h2>How Google Review Sharing Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Add Your Link</h4>
              <p>Paste your Google Places review URL into the management console. One field, one time — that's it.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>Guest Chats</h4>
              <p>Guests tap in at the table and chat normally. When they express genuine enthusiasm, TableChat includes your review link naturally.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Reviews Grow</h4>
              <p>Happy guests leave reviews in the moment — when the experience is fresh and the sentiment is real.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-cta">
        <div className="container">
          <h2>Your Best Reviews Come from Your Best Moments</h2>
          <p>Stop hoping guests remember to leave a review after they leave. Capture that positive energy while they're still at the table.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
};

export default GoogleReviews;
